import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FwPopoverComponent } from '@flywheel-io/vision';
import { Observable, Subject } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

import { OperationFeedbackItemDownloadStatus } from '../operation-feedback.enums';
import { OperationFeedbackItemData } from '../operation-feedback.interfaces';
import { isChrome } from '../../../../shared/browser.utils';

@Component({
  host: {
    'test-id': 'operation-feedback-item',
  },
  selector: 'operation-feedback-item',
  templateUrl: './operation-feedback-item.component.html',
  styleUrls: ['./operation-feedback-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationFeedbackItem {
  @Input()
  set data(value: OperationFeedbackItemData) {
    this._data = value;
    let downloaded = 0;
    let total = 0;
    if (value.downloadedSize && value.totalSize) {
      downloaded = value.downloadedSize;
      total = value.totalSize;
    } else if (value.downloadedFiles && value.totalFiles) {
      downloaded = value.downloadedFiles;
      total = value.totalFiles;
    }
    const progressValueBase = total && downloaded ? downloaded / total : 0;
    this.progressValue = Math.ceil(progressValueBase * 100)
    this.timeLeft$.next(this.calcTimeLeft(value));
    this.currentFileShortName = this.truncateMiddle(this.generateCurrentFileShortName(value.currentFile), this.maxDescriptionLength)
    this.title = this.truncateMiddle(value.title, this.maxTitleLength)
  }

  get data(): OperationFeedbackItemData {
    return this._data;
  }

  @Output() readonly refresh = new EventEmitter<OperationFeedbackItemData>();
  @Output() readonly cancel = new EventEmitter<OperationFeedbackItemData>();

  @ViewChild('popoverTrigger') popoverTrigger: ElementRef;
  @ViewChild(FwPopoverComponent) popover: FwPopoverComponent;

  private _data: OperationFeedbackItemData;

  protected readonly operationFeedbackItemDownloadStatus = OperationFeedbackItemDownloadStatus;
  protected readonly isChrome = isChrome();
  protected progressValue = 0;
  private readonly timeLeft$ = new Subject<number>();
  protected timeLeftData$: Observable<number>;
  protected currentFileShortName = '';
  protected title = '';
  private emaSpeed = 0;
  private emaAlpha = 0.2;
  private readonly throttleDelay = 3000
  private readonly oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  private readonly maxTitleLength = 55;
  private readonly maxDescriptionLength = 75;

  constructor() {
    this.timeLeftData$ = this.timeLeft$.pipe(filter((v) => Boolean(v) && v < this.oneWeekMs), throttleTime(this.throttleDelay, undefined, { leading: true, trailing: true }));
  }

  private calcTimeLeft(value: OperationFeedbackItemData): number {
    const now = Date.now();
    const start = new Date(value.start).getTime();
    const total = value.totalSize && value.totalFiles
      ? value.totalSize * value.totalFiles
      : value.totalSize || value.totalFiles;
    const downloaded = value.downloadedSize && value.downloadedFiles
      ? value.downloadedSize * value.downloadedFiles
      : value.downloadedSize || value.downloadedFiles;

    if (!total || !downloaded || downloaded <= 0) {
      return 0;
    }

    const elapsed = (now - start) / 1000;
    const instantSpeed = downloaded / elapsed;

    if (this.emaSpeed === 0) {
      this.emaSpeed = instantSpeed;
    } else {
      this.emaSpeed =
        this.emaAlpha * instantSpeed + (1 - this.emaAlpha) * this.emaSpeed;
    }

    const remaining = total - downloaded;
    const timeLeft = this.emaSpeed > 0 ? remaining / this.emaSpeed : 0;

    return Math.max(0, timeLeft * 1000);
  }

  protected closePopover(): void {
    if (!this.popover.isOpen) {
      return;
    }
    this.popoverTrigger.nativeElement.click();
  }

  protected onCancelClick(): void {
    if (this.data.status === OperationFeedbackItemDownloadStatus.Progress) {
      this.popoverTrigger.nativeElement.click();
      return;
    }
    this.closePopover();
    this.cancel.emit(this.data);
  }

  protected generateCurrentFileShortName(fileName: string): string {
    if (!fileName) {
      return '';
    }

    return fileName.split('/').pop();
  }

  protected truncateMiddle(filename: string, maxLength: number): string {
    if (filename.length <= maxLength) {
      return filename;
    }

    const extIndex = filename.lastIndexOf('.');
    const extension = extIndex !== -1 ? filename.slice(extIndex) : '';
    const nameOnly = extIndex !== -1 ? filename.slice(0, extIndex) : filename;
    const charsToShow = maxLength - extension.length - 3;
    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    const truncated =
      nameOnly.slice(0, frontChars) +
      '...' +
      nameOnly.slice(nameOnly.length - backChars);

    return truncated + extension;
  }
}
